import { POOL_NAMES_BY_POOL_ID } from 'src/constants/constants';
import { MAIN_POOL_ID } from '@velocity-exchange/common';

export type MarketSymbolContext = 'borrow_lend' | 'insurance_fund' | 'clean';

/**
 * Extracts pool ID from market symbol
 * @param marketSymbol Market symbol (e.g. "USDC-1")
 * @returns Pool ID or undefined
 */
function extractPoolIdFromSymbol(marketSymbol: string): number | undefined {
	const match = marketSymbol.match(/-([0-9]+)$/);
	return match ? parseInt(match[1], 10) : undefined;
}

/**
 * Formats market symbol based on context
 * @param marketSymbol Original market symbol (e.g. "USDC-1", "JLP-1")
 * @param context Usage context
 * @returns Formatted symbol
 *
 * @example
 * formatMarketSymbolByContext('USDC-1', 'borrow-lend') // "USDC [JLP Market]"
 * formatMarketSymbolByContext('USDC-1', 'insurance-fund') // "USDC Vault [JLP Market]"
 * formatMarketSymbolByContext('USDC-1', 'clean') // "USDC"
 */
export function formatMarketSymbolByContext(
	marketSymbol: string,
	context: MarketSymbolContext = 'clean'
): string {
	// Remove -number from base symbol
	const baseSymbol = marketSymbol.replace(/-[0-9]+$/, '');

	// Extract pool ID
	const poolId = extractPoolIdFromSymbol(marketSymbol);

	// Return base symbol only if Main Market (poolId 0) or no pool ID
	if (!poolId || poolId === MAIN_POOL_ID) {
		switch (context) {
			case 'insurance_fund':
				return `${baseSymbol} Vault`;
			case 'borrow_lend':
			case 'clean':
			default:
				return baseSymbol;
		}
	}

	// Get pool name
	const poolName = POOL_NAMES_BY_POOL_ID[poolId];
	if (!poolName) {
		// Return default format if pool name not found
		switch (context) {
			case 'insurance_fund':
				return `${baseSymbol} Vault`;
			case 'borrow_lend':
			case 'clean':
			default:
				return baseSymbol;
		}
	}

	// Format based on context
	switch (context) {
		case 'borrow_lend':
			return `${baseSymbol} [${poolName}]`;
		case 'insurance_fund':
			return `${baseSymbol} Vault`;
		case 'clean':
		default:
			return baseSymbol;
	}
}
